'use client';

/**
 * NETWORK — Beast Mind NEURAL WEB spec, VERBATIM math:
 *   elliptical orbit projection (y·.38), depth = cos(a+rot)·.5+.5 drives
 *   scale/opacity/z, quadratic edges lifted -26px, drag·rotate, scroll·zoom,
 *   pinch node → fly (scale ×2.2 + zoom ×1.7) then navigate to its surface.
 * Nodes are the REAL empire: BEAST core → PROJECTS/CONTENT/EMAIL/CRM hubs →
 * 6 business groups → their live sites/funnels from the same registry+APIs
 * as classic.
 */
import { useEffect, useRef, useState } from 'react';
import { BUSINESS_PROJECTS } from '@/lib/business-projects';
import { mono } from '@/future/primitives';

type View = { deck: string } | { surface: string };
type Node = { t: string; tier: 0 | 1 | 2 | 3; a: number; r: number; hex: string; rgb: string; big: number; parent?: number; opens?: View };

const BIZ_HUE: Record<string, { hex: string; rgb: string }> = {
  wtf: { hex: '#ffb454', rgb: '255,180,84' },
  charters: { hex: '#e6c88a', rgb: '230,200,138' },
  olga: { hex: '#b49aff', rgb: '180,154,255' },
  astro: { hex: '#ff9ad5', rgb: '255,154,213' },
  vctronics: { hex: '#7db4ff', rgb: '125,180,255' },
  collabs: { hex: '#f2a0b5', rgb: '242,160,181' },
};

export function NetworkSurface({ navigate }: { navigate?: (v: View) => void }) {
  const [rot, setRot] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [fly, setFly] = useState<number | null>(null);
  const [wtfSites, setWtfSites] = useState<string[]>([]);
  const drag = useRef<{ x: number; moved: boolean } | null>(null);

  useEffect(() => {
    // top factory sites for the WV leaves — same /api/portfolio as classic
    fetch('/api/portfolio').then(r => r.json())
      .then(d => setWtfSites((Array.isArray(d) ? d : []).slice(0, 3).map((s: any) => s.domain || s.slug)))
      .catch(() => {});
  }, []);

  // ── node defs (netNodesDef verbatim shape, real registry data) ─────────────
  const N: Node[] = [{ t: 'BEAST', tier: 0, a: 0, r: 0, hex: '#9ef0ff', rgb: '158,240,255', big: 22 }];
  ([['PROJECTS', -90, { deck: 'projects' }], ['CONTENT', 0, { surface: 'content' }], ['EMAIL', 90, { deck: 'email' }], ['CRM', 180, { surface: 'crm' }]] as [string, number, View][])
    .forEach(([t, a, opens]) => N.push({ t, tier: 1, a, r: 205, hex: '#7de8ff', rgb: '125,232,255', big: 11, parent: 0, opens }));
  const main = BUSINESS_PROJECTS.filter(p => !p.slug.startsWith('collab-'));
  main.forEach((p, i) => {
    const hue = BIZ_HUE[p.slug] || BIZ_HUE.collabs;
    N.push({ t: p.name.toUpperCase(), tier: 2, a: -150 + i * 60, r: 400, ...hue, big: 8, parent: 1, opens: { deck: `tools:${p.slug}` } });
  });
  N.push({ t: 'COLLABS', tier: 2, a: 165, r: 400, ...BIZ_HUE.collabs, big: 8, parent: 1, opens: { deck: 'tools:collabs' } });
  // leaves: real sites + funnels per business
  main.forEach((p, i) => {
    const parent = 5 + i;
    const par = N[parent];
    const baseA = -150 + i * 60;
    const doms = p.sites === 'factory' ? wtfSites : p.sites.slice(0, 2).map(s => s.domain);
    doms.forEach((d, k) => N.push({ t: d.toUpperCase(), tier: 3, a: baseA - 18 + k * 18, r: 610, hex: par.hex, rgb: par.rgb, big: 5, parent, opens: { surface: `websites:${p.slug}` } }));
    if (p.funnelId) N.push({ t: 'FUNNEL · VSL', tier: 3, a: baseA + 18 + doms.length * 2, r: 610, hex: par.hex, rgb: par.rgb, big: 5, parent, opens: { surface: `funnel:${p.slug}` } });
  });

  // ── projection (netVals verbatim) ──────────────────────────────────────────
  const cx = 720, cy = 430;
  const pos = N.map(n => {
    const rad = (n.a + rot) * Math.PI / 180;
    const depth = n.tier === 0 ? 1 : Math.cos(rad) * .5 + .5;
    return { x: cx + Math.cos(rad) * n.r * .75, y: cy + Math.sin(rad) * n.r * .38 * .75 - (n.tier === 1 ? 30 : 0), depth };
  });

  const flyTo = (i: number) => {
    const n = N[i];
    if (n.tier === 0 || !n.opens || !navigate) return;
    setFly(i);
    setTimeout(() => navigate(n.opens!), 750);
  };

  return (
    <div className="absolute inset-0 overflow-hidden" style={{ cursor: 'grab' }}
      onPointerDown={e => { drag.current = { x: e.clientX, moved: false }; }}
      onPointerMove={e => {
        if (!drag.current) return;
        const dx = e.clientX - drag.current.x;
        if (Math.abs(dx) > 2) {
          drag.current.moved = true;
          setRot(r => r + dx * .35);
          drag.current.x = e.clientX;
        }
      }}
      onPointerUp={() => { setTimeout(() => { drag.current = null; }, 0); }}
      onWheel={e => setZoom(z => Math.max(.65, Math.min(1.7, z + (e.deltaY > 0 ? -.09 : .09))))}>
      {/* title */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, textAlign: 'center', pointerEvents: 'none', zIndex: 30 }}>
        <div style={{ fontWeight: 200, fontSize: 56, letterSpacing: '.14em', color: '#e9f9ff', textShadow: '0 0 18px rgba(0,240,255,.25)' }}>NEURAL WEB</div>
        <div style={{ ...mono(17, '.3em'), color: '#56788c', marginTop: 8 }}>THE EMPIRE AS ONE ORGANISM · {N.length} NODES</div>
      </div>

      {/* zoomable web */}
      <div style={{ position: 'absolute', inset: 0, transform: `scale(${fly != null ? zoom * 1.7 : zoom})`, transformOrigin: '50% 52%', opacity: fly != null ? .9 : 1, transition: fly != null ? 'transform .75s cubic-bezier(.6,0,.9,.5)' : 'transform .25s ease-out' }}>
        <svg width="100%" height="100%" style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}>
          {N.map((n, i) => {
            if (n.parent == null) return null;
            const a = pos[n.parent], b = pos[i];
            const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2 - 26;
            return <path key={i} d={`M${a.x.toFixed(1)} ${a.y.toFixed(1)} Q${mx.toFixed(1)} ${my.toFixed(1)} ${b.x.toFixed(1)} ${b.y.toFixed(1)}`} fill="none" stroke={`rgba(${n.rgb},.5)`} strokeWidth="1" opacity={(.12 + pos[i].depth * .3).toFixed(2)} />;
          })}
        </svg>
        {N.map((n, i) => {
          const p = pos[i];
          const sc = .72 + p.depth * .45;
          return (
            <div key={i}
              onMouseUp={() => { if (!drag.current?.moved) flyTo(i); }}
              style={{
                position: 'absolute', left: p.x, top: p.y, display: 'flex', alignItems: 'center', gap: 9,
                transform: `translate(-50%,-50%) scale(${fly === i ? sc * 2.2 : sc})`,
                opacity: fly != null ? (fly === i ? 1 : .12) : (.45 + p.depth * .55),
                zIndex: 10 + Math.round(p.depth * 10),
                pointerEvents: fly == null ? 'auto' : 'none',
                transition: fly != null ? 'transform .7s cubic-bezier(.6,0,.9,.5),opacity .6s' : 'opacity .2s',
              }}>
              <span style={{ width: n.big * 2, height: n.big * 2, borderRadius: '50%', background: `radial-gradient(circle at 35% 30%, #eafcff, ${n.hex} 55%, rgba(6,14,22,.9))`, boxShadow: `0 0 ${n.big * 3}px rgba(${n.rgb},.7)`, flex: 'none', animation: n.tier === 0 ? 'uifBreathe 4.5s ease-in-out infinite' : 'none' }} />
              <span style={{ ...mono(n.tier <= 1 ? 8.5 : n.tier === 2 ? 8 : 6.5, '.18em'), color: n.tier === 3 ? '#7fa9bd' : '#d7f2ff', textShadow: `0 0 10px rgba(${n.rgb},.5)`, whiteSpace: 'nowrap' }}>{n.t}</span>
            </div>
          );
        })}
      </div>

      {/* legend pill */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 24, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
        <span style={{ padding: '7px 18px', borderRadius: 999, border: '1px solid rgba(125,232,255,.3)', background: 'rgba(0,240,255,.05)', ...mono(18, '.12em'), color: '#9ef0ff', textShadow: '0 0 10px rgba(0,240,255,.4)' }}>
          DRAG · ROTATE&nbsp;&nbsp;&nbsp;SCROLL · ZOOM&nbsp;&nbsp;&nbsp;PINCH NODE · FLY TO ITS PAGE
        </span>
      </div>
    </div>
  );
}
